import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from './axios';
import { useNavigate } from 'react-router-dom';

const AuthContext = createContext();

export function useAuth() {
  return useContext(AuthContext);
}

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  // Memoized function to verify authentication with the token
  const verifyAuthentication = useCallback(async (authToken) => {
    if (!authToken) return false;
    
    // Try to get stored role from localStorage
    const storedUserData = localStorage.getItem('userData');
    let userData = null;
    let userRole = null;
    
    if (storedUserData) {
      try {
        userData = JSON.parse(storedUserData);
        userRole = userData.role;
      } catch (e) {
        console.log("Could not parse stored user data");
      }
    }

    let isAuthenticated = false;

    // Try authentication based on stored role first
    if (userRole) {
      try {
        let endpoint = '/auth/me'; // Default endpoint
        
        if (userRole === 'doctor') {
          endpoint = '/doctors/me';
        } else if (userRole === 'admin') {
          endpoint = '/admin/me';
        }
        
        const response = await axios.get(endpoint, {
          headers: { Authorization: `Bearer ${authToken}` }
        });
        
        if (response.data && response.data.data) {
          const verifiedUserData = {
            ...response.data.data,
            role: userRole
          };
          setCurrentUser(verifiedUserData);
          localStorage.setItem('userData', JSON.stringify(verifiedUserData));
          return true;
        }
      } catch (error) {
        console.log(`${userRole} authentication failed:`, error.message);
      }
    }

    // If we're still not authenticated, try each endpoint in sequence
    // 1. Try regular user
    try {
      const userResponse = await axios.get('/auth/me', {
        headers: { Authorization: `Bearer ${authToken}` }
      });
      
      if (userResponse.data && userResponse.data.data) {
        const userData = {
          ...userResponse.data.data,
          role: userResponse.data.data.role || 'patient'
        };
        setCurrentUser(userData);
        localStorage.setItem('userData', JSON.stringify(userData));
        return true;
      }
    } catch (error) {
      console.log("Regular user authentication failed:", error.message);
    }

    // 2. Try doctor
    try {
      const doctorResponse = await axios.get('/doctors/me', {
        headers: { Authorization: `Bearer ${authToken}` }
      });
      
      if (doctorResponse.data && doctorResponse.data.data) {
        const doctorData = {
          ...doctorResponse.data.data,
          role: 'doctor'
        };
        setCurrentUser(doctorData);
        localStorage.setItem('userData', JSON.stringify(doctorData));
        return true;
      }
    } catch (error) {
      console.log("Doctor authentication failed:", error.message);
    }

    // 3. Try admin
    try {
      const adminResponse = await axios.get('/admin/me', {
        headers: { Authorization: `Bearer ${authToken}` }
      });
      
      if (adminResponse.data && adminResponse.data.data) {
        const adminData = {
          ...adminResponse.data.data,
          role: 'admin'
        };
        setCurrentUser(adminData);
        localStorage.setItem('userData', JSON.stringify(adminData));
        return true;
      }
    } catch (error) {
      console.log("Admin authentication failed:", error.message);
    }

    // If we reach here, all authentication attempts failed
    return false;
  }, []);

  // Set auth token for all requests
  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    } else {
      delete axios.defaults.headers.common['Authorization'];
    }
  }, [token]);
  
  // Check if user is logged in on app startup
  useEffect(() => {
    const checkLoggedIn = async () => {
      if (!token) {
        setLoading(false);
        return;
      }

      const isAuthenticated = await verifyAuthentication(token);
      
      if (!isAuthenticated) {
        console.log("Authentication failed completely, clearing token");
        localStorage.removeItem('token');
        localStorage.removeItem('userData');
        setToken(null);
      }
      
      setLoading(false);
    };
    
    checkLoggedIn();
  }, [token, verifyAuthentication]);

  // Login function - fixed to prevent double login
  const login = async (email, password) => {
    setLoading(true);
    
    try {
      // First try regular login endpoint
      try {
        const response = await axios.post('/auth/login', { email, password });
        const { token: newToken, user } = response.data;
        
        // Set token in axios before verification
        axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
        document.cookie = `authToken=${newToken}; path=/; max-age=2592000; SameSite=Strict`; // 30-day cookie
        // Store data first
        localStorage.setItem('token', newToken);
        localStorage.setItem('userData', JSON.stringify(user));
        
        // Update state
        setToken(newToken);
        setCurrentUser(user);
        
        setLoading(false);
        return user;
      } catch (userError) {
        // If regular login fails, try doctor login
        console.log("Regular login failed, trying doctor login");
        const doctorResponse = await axios.post('/doctors/login', { email, password });
        document.cookie = `authToken=${newToken}; path=/; max-age=2592000; SameSite=Strict`; // 30-day cookie

        const { token: newToken, doctor } = doctorResponse.data;
        const doctorWithRole = { ...doctor, role: 'doctor' };
        
        // Set token in axios before verification
        axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
        
        // Store data first
        localStorage.setItem('token', newToken);
        localStorage.setItem('userData', JSON.stringify(doctorWithRole));
        
        // Update state
        setToken(newToken);
        setCurrentUser(doctorWithRole);
        
        setLoading(false);
        return doctorWithRole;
      }
    } catch (error) {
      setLoading(false);
      console.error("Login failed completely:", error);
      throw error;
    }
  };

  // Register function
  const register = async (userData) => {
    try {
      const response = await axios.post('/auth/register', userData);
      return response.data;
    } catch (error) {
      throw error;
    }
  };

  // Logout function
  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('userData');
    setToken(null);
    setCurrentUser(null);
    navigate('/login');
  };

  // Context value
  const value = {
    currentUser,
    login,
    register,
    logout,
    loading,
    isAuthenticated: !!currentUser
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading ? children : (
        <div className="flex items-center justify-center min-h-screen">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
        </div>
      )}
    </AuthContext.Provider>
  );
};

export default AuthContext;